import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, CaretDown, CaretRight, MagnifyingGlass } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useWorkspace, useWorkspaces, useGrcHealth } from "@/lib/queries";
import { LIFECYCLE_STAGES, TAB_MAP, STAGE_INDEX_OF, resolvePill, stageLandingId, workspaceKeyFromPath, workspacePath } from "@/lib/workspaceNav";
import { getRecents } from "@/lib/recents";
import { useSmartBack } from "@/lib/useSmartBack";
import { OrgLogo } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

const HEALTH_DOT = {
  Healthy: "bg-success", Stable: "bg-primary", Attention: "bg-warning",
  "At Risk": "bg-destructive", "No data": "bg-shell-muted",
};

/** Fast client switcher used inside the workspace nav header. */
function ClientSwitcherPanel({ activeId, onNavigate }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const { data } = useWorkspaces({ page: 1, pageSize: 100, filters: { status: "Active" } });
  const items = data?.items || [];
  const recentIds = getRecents().filter((r) => r.type === "workspace").map((r) => r.id);
  const filtered = items.filter((w) => !q.trim() ||
    `${w.name} ${w.client_name || ""} ${w.sector || ""}`.toLowerCase().includes(q.toLowerCase()));
  const recents = filtered.filter((w) => recentIds.includes(w.id)).sort((a, b) => recentIds.indexOf(a.id) - recentIds.indexOf(b.id));
  const rest = filtered.filter((w) => !recentIds.includes(w.id));
  const go = (w) => { navigate(workspacePath(w)); onNavigate?.(); };

  const Row = ({ w }) => (
    <button data-testid={`wsnav-switch-${w.id}`} onClick={() => go(w)}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-shell-surface-hover">
      <span className="shrink-0"><OrgLogo name={w.client_name || w.name} website={w.website} logoUrl={w.logo_data_url} size={24} /></span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-shell-text">{w.name}</span>
        {w.sector && <span className="block truncate text-[11px] text-shell-muted">{w.sector}</span>}
      </span>
      {w.id === activeId && <span className="h-1.5 w-1.5 rounded-full bg-accent-ai" />}
    </button>
  );

  return (
    <PopoverContent align="start" sideOffset={6}
      className="w-72 rounded-none border-shell-border bg-shell-base p-0 text-shell-text shadow-shell-lg" data-testid="wsnav-switcher-panel">
      <div className="flex items-center gap-2 border-b border-shell-border px-3 py-2">
        <MagnifyingGlass className="h-4 w-4 text-shell-muted" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Jump to client…"
          data-testid="wsnav-switcher-input" className="w-full bg-transparent text-sm text-shell-text outline-none placeholder:text-shell-muted" />
      </div>
      <div className="max-h-72 overflow-y-auto py-1">
        {recents.length > 0 && (
          <>
            <p className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wide text-shell-muted">Recent</p>
            {recents.map((w) => <Row key={w.id} w={w} />)}
            {rest.length > 0 && <p className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wide text-shell-muted">All clients</p>}
          </>
        )}
        {rest.map((w) => <Row key={w.id} w={w} />)}
        {filtered.length === 0 && <p className="px-3 py-4 text-center text-sm text-shell-muted">No clients found.</p>}
      </div>
    </PopoverContent>
  );
}

function HealthDot({ wid }) {
  const { data: h } = useGrcHealth(wid);
  const level = h?.level || "No data";
  return <span className={cn("h-2 w-2 shrink-0 rounded-full", HEALTH_DOT[level] || HEALTH_DOT["No data"], level === "At Risk" && "pulse-ai")} />;
}

function SubTabLink({ id, active, onGo }) {
  const t = TAB_MAP[id];
  if (!t) return null;
  return (
    <button data-testid={`wsnav-tab-${id}`} onClick={() => onGo(id)} aria-current={active ? "page" : undefined}
      className={cn(
        "group relative flex w-full items-center gap-2.5 py-1.5 pl-9 pr-3 text-left text-[13px] transition-colors",
        active ? "font-semibold text-sidebar-foreground" : "text-sidebar-muted hover:text-sidebar-foreground"
      )}>
      {active && <span className="absolute left-[26px] top-1/2 h-4 w-[2px] -translate-y-1/2 bg-accent-ai" />}
      <t.icon className={cn("h-4 w-4 shrink-0", active ? "text-accent-ai" : "text-sidebar-muted/70 group-hover:text-sidebar-foreground")} weight={active ? "fill" : "regular"} />
      <span className="truncate">{t.label}</span>
    </button>
  );
}

/**
 * Context-Aware workspace navigation. When a firm user is inside a client workspace the global
 * sidebar transforms into THIS: a back-to-firm header + fast client switcher, then the 5
 * lifecycle stages as a vertical accordion whose sub-tabs drive ?tab=. Reduces the old dual
 * horizontal stepper+pills to one calm vertical spine.
 */
export function WorkspaceNavPanel({ collapsed = false, onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const back = useSmartBack("/workspaces");
  const id = workspaceKeyFromPath(location.pathname);
  const { data } = useWorkspace(id);
  const ws = data?.workspace;

  const currentTab = new URLSearchParams(location.search).get("tab") || "overview";
  const activePill = resolvePill(currentTab);
  const activeStageIndex = STAGE_INDEX_OF(activePill);
  const [openStage, setOpenStage] = useState(activeStageIndex);
  useEffect(() => { setOpenStage(activeStageIndex); }, [activeStageIndex]);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const go = (tab) => { navigate(`${location.pathname}?tab=${tab}`, { replace: true }); onNavigate?.(); };
  const goBack = () => { back(); onNavigate?.(); };
  const name = ws?.name || "Client workspace";

  // Collapsed icon rail — one stage icon per row, jumps to the stage's first tab.
  if (collapsed) {
    return (
      <div className="flex h-full flex-col bg-shell-base" data-testid="workspace-nav-panel">
        <button onClick={goBack} title="Back to previous page"
          data-testid="wsnav-back" className="flex items-center justify-center border-b border-shell-border py-3 text-shell-muted transition-colors hover:text-shell-text">
          <ArrowLeft className="h-4 w-4" weight="bold" />
        </button>
        <nav className="flex-1 space-y-1 overflow-y-auto py-2">
          {LIFECYCLE_STAGES.map((s, i) => {
            const on = i === activeStageIndex;
            return (
              <button key={s.key} onClick={() => go(stageLandingId(s.key))} title={`${s.num} · ${s.label}`} data-testid={`wsnav-stage-${s.key}`}
                className={cn("relative mx-2 flex items-center justify-center rounded-[2px] py-2.5 transition-colors",
                  on ? "bg-card text-foreground ring-1 ring-border" : "text-sidebar-muted hover:bg-sidebar-hover/70 hover:text-sidebar-foreground")}>
                <s.icon className={cn("h-[18px] w-[18px]", on && "text-accent-ai")} weight={on ? "fill" : "bold"} />
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-shell-base" data-testid="workspace-nav-panel">
      {/* Back to previous page (falls back to the client list on a direct deep-link) */}
      <button onClick={goBack} data-testid="wsnav-back"
        className="flex items-center gap-2 border-b border-shell-border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-shell-muted transition-colors hover:text-shell-text">
        <ArrowLeft className="h-3.5 w-3.5" weight="bold" /> Back
      </button>

      {/* Active client identity + switcher */}
      <div className="border-b border-shell-border px-3 py-3">
        <p className="mb-1.5 px-1 font-mono text-[9px] uppercase tracking-[0.2em] text-shell-muted">Active Client</p>
        <Popover open={switcherOpen} onOpenChange={setSwitcherOpen}>
          <PopoverTrigger asChild>
            <button data-testid="wsnav-switcher-trigger"
              className="group flex w-full items-center gap-2.5 border border-shell-border bg-shell-surface px-2.5 py-2 text-left transition-all hover:border-accent-ai/40 hover:bg-shell-surface-hover">
              <span className="shrink-0"><OrgLogo name={ws?.client_name || name} website={ws?.website} logoUrl={ws?.logo_data_url} size={32} /></span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-semibold text-shell-text" data-testid="wsnav-client-name">{name}</span>
                  {ws?.id && <HealthDot wid={ws.id} />}
                </span>
                <span className="block truncate font-mono text-[9px] uppercase tracking-wide text-shell-muted">{ws?.sector || "Client workspace"}</span>
              </span>
              <CaretDown className="h-4 w-4 shrink-0 text-shell-muted transition-colors group-hover:text-accent-ai" weight="bold" />
            </button>
          </PopoverTrigger>
          <ClientSwitcherPanel activeId={ws?.id} onNavigate={() => { setSwitcherOpen(false); onNavigate?.(); }} />
        </Popover>
      </div>

      {/* Lifecycle accordion */}
      <nav className="flex-1 overflow-y-auto py-1" aria-label="Workspace sections" data-testid="wsnav-accordion">
        {LIFECYCLE_STAGES.map((s, i) => {
          const expanded = openStage === i;
          const isCurrent = i === activeStageIndex;
          return (
            <div key={s.key} className="border-b border-shell-border/50">
              <div className={cn("flex w-full items-center transition-colors",
                isCurrent ? "text-sidebar-foreground" : "text-sidebar-muted")}>
                <button onClick={() => { go(stageLandingId(s.key)); setOpenStage(i); }} data-testid={`wsnav-stage-${s.key}`}
                  className="flex flex-1 items-center gap-2.5 py-2.5 pl-4 pr-1 text-left transition-colors hover:text-sidebar-foreground">
                  <s.icon className={cn("h-[18px] w-[18px] shrink-0", isCurrent ? "text-accent-ai" : "")} weight={isCurrent ? "fill" : "bold"} />
                  {s.num && <span className={cn("font-mono text-[10px]", isCurrent ? "text-accent-ai" : "text-shell-muted/70")}>{s.num}</span>}
                  <span className="flex-1 truncate text-sm font-medium">{s.label}</span>
                </button>
                <button onClick={() => setOpenStage(expanded ? -1 : i)} data-testid={`wsnav-stage-toggle-${s.key}`}
                  aria-expanded={expanded} aria-label={`${expanded ? "Collapse" : "Expand"} ${s.label} sub-sections`}
                  className="flex items-center px-3 py-2.5 text-shell-muted transition-colors hover:text-sidebar-foreground">
                  <CaretRight className={cn("h-3.5 w-3.5 shrink-0 transition-transform", expanded && "rotate-90")} weight="bold" />
                </button>
              </div>
              {expanded && (
                <div className="pb-1.5">
                  {s.ids.map((tid) => <SubTabLink key={tid} id={tid} active={activePill === tid} onGo={go} />)}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

export default WorkspaceNavPanel;
